/**
 * `/admin/configuracoes/*` — a CASCA da Configurações (K-1 da rodada CASCA).
 *
 * ── O que esta tela era, e o que ela virou
 *
 * Nasceu como **índice**: listava as configurações e mandava a pessoa embora
 * para a tela antiga, com o cabeçalho e a navegação antigos. O Matheus clicou em
 * "Departamentos", caiu na tela velha e disse o óbvio — *"eu imaginei que essa UI
 * não existiria mais"*. Ele estava certo.
 *
 * Agora é **casca**: o menu de seções fica à esquerda, sempre visível, e o
 * conteúdo renderiza no painel direito via `<Outlet/>`. Nunca se sai da
 * Configurações.
 *
 * ── A divisão de trabalho, e por que ela é assim
 *
 * - **este arquivo** é só a moldura: cabeçalho, busca, menu, `<Outlet/>`;
 * - **`SecaoIndice`** é o que o painel mostra quando nenhum item foi escolhido —
 *   a lista de itens da seção;
 * - o conteúdo de cada item entra pelas rotas filhas.
 *
 * A moldura **não remonta** ao trocar de item: é o ponto da casca. Se o menu
 * piscasse a cada clique, teríamos trocado uma navegação ruim por outra.
 *
 * ── Regras que esta tela respeita, e por quê
 *
 * - **Zero emoji.** Ícone é SVG do lucide herdando `currentColor`. Emoji muda de
 *   desenho por sistema operacional e não obedece a cor do tema.
 * - **A seção vive na URL**, nunca em localStorage — dá pra mandar alguém direto
 *   em Financeiro em vez de explicar o caminho.
 * - **Filtro por permissão**: quem não é admin só vê o que a chave de acesso
 *   dele libera.
 *
 * ⚠️ O cabeçalho antigo afirmava aqui que "o catálogo é conferido por teste
 * contra o `App.tsx`". **Não era** — ver a nota em `catalogo.ts`. O guarda de
 * verdade entra no K-2.
 */
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import {
  Building2,
  DollarSign,
  Key,
  Plug,
  Settings,
  Tag,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';

import { PageHeader, PageContainer, EmptyState, HelpTip, Select, SelectOption } from '@/components/ui';
import { cn } from '@/lib/cn';
import { type IconeSecao } from './catalogo';
import { ajudaDaSecao, useSecoesPermitidas } from './secoes';
import { CascaProvider } from './casca-contexto';
import { useCasca } from './casca-contrato';

/** Ícone por nome — Lucide, nunca emoji. */
const ICONES: Record<IconeSecao, LucideIcon> = {
  key: Key,
  tag: Tag,
  'trending-up': TrendingUp,
  'dollar-sign': DollarSign,
  settings: Settings,
  plug: Plug,
  building: Building2,
};

/**
 * A casca é envolvida pelo `CascaProvider` (K-4): é ele que faz o `PageHeader` e
 * o `PageContainer` das telas embutidas se calarem, e que carrega a ação da tela
 * filha até o cabeçalho aqui de cima.
 */
export function ConfiguracoesPage() {
  return (
    <CascaProvider>
      <ConfiguracoesCasca />
    </CascaProvider>
  );
}

function ConfiguracoesCasca() {
  const { secao: secaoParam, item: itemParam } = useParams<{ secao?: string; item?: string }>();
  const { acoes } = useCasca();
  const navigate = useNavigate();

  // S-4 (BUSCA-UNICA, decisão do Matheus): a barra local de busca SAIU — o
  // sistema tem UMA busca, a da TopBar (Ctrl+K), que cobre estes itens com os
  // mesmos sinônimos e o mesmo filtro de permissão. O `?q=` da URL (G-4d)
  // morreu junto: era o endereço da busca local, e ela não existe mais.

  // Ponto ÚNICO — o mesmo hook que o painel usa. Duplicar o cálculo abriria a
  // chance de menu e painel discordarem sobre quais seções existem.
  const secoes = useSecoesPermitidas();
  const atual = secoes.find((s) => s.id === secaoParam) ?? secoes[0];
  const itemAberto = itemParam ? atual?.itens.find((i) => i.id === itemParam) : undefined;

  if (!atual) {
    return (
      <PageContainer daCasca>
        <PageHeader daCasca crumb="Configurações" title="Configurações" />
        <EmptyState
          title="Nada para configurar aqui"
          description="Seu acesso não inclui nenhuma configuração. Se precisa de alguma, peça ao administrador."
        />
      </PageContainer>
    );
  }

  return (
    /* G-2: a tela traz a PRÓPRIA casca (`PageContainer`). Ela entrou na lista de
       rotas de módulo do `AdminLayout`, então a barra de abas antiga não
       renderiza mais por cima — e sem o container do layout, o padding teria de
       vir daqui. */
    <PageContainer daCasca>

      {/* S-4: a busca local que morava aqui saiu — buscar configuração agora
          é na barra do topo (Ctrl+K), junto com clientes, propostas e OS. */}
      <div className="flex min-w-0 flex-col gap-6 sm:flex-row">
        {/* ── Menu de seções ───────────────────────────────────────────── */}
        <div className="w-full shrink-0 sm:w-60">
          {/* D-2 (#70): em 375px a fileira de pílulas estourava a tela em 37px
              e só 2 das 6 seções ficavam visíveis. Abaixo de 640px o menu vira
              um <Select> canônico de UMA linha — as 6 seções a um toque, e o
              conteúdo ganha a tela inteira. No desktop nada muda. */}
          <div className="sm:hidden">
            <Select
              value={atual.id}
              onChange={(e) => navigate(`/admin/configuracoes/${e.target.value}`)}
              aria-label="Seção de configuração"
              data-testid="config-secao-select"
            >
              {secoes.map((s) => (
                <SelectOption key={s.id} value={s.id}>
                  {s.nome} ({s.itens.length})
                </SelectOption>
              ))}
            </Select>
          </div>
          <nav aria-label="Seções de configuração" className="hidden sm:block">
            <ul className="flex flex-col gap-1">
              {secoes.map((s) => {
                const Icone = ICONES[s.icone];
                const ativa = s.id === atual.id;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/configuracoes/${s.id}`)}
                      aria-current={ativa ? 'page' : undefined}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm',
                        ativa
                          ? 'bg-red/10 font-medium text-red'
                          : 'text-ink-2 hover:bg-ink-1/5 hover:text-ink-1',
                      )}
                    >
                      {/* G-4e: `text-ink-2`, não `ink-3` — o ícone estava com
                          contraste baixo demais e o de Integrações ficava
                          ilegível no tamanho real. Herda `currentColor`, então
                          acompanha o estado ativo. */}
                      <Icone className="h-4 w-4 shrink-0" aria-hidden="true" />
                      <span className="truncate">{s.nome}</span>
                      <span className="ml-auto text-xs text-ink-2">{s.itens.length}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>

        {/* ── Painel: resultados da busca OU o que a rota filha mandar ────
            A busca ganha do `<Outlet/>` de propósito: quem digitou está
            procurando, e manter o item aberto por baixo do resultado só
            competiria com o que a pessoa pediu. */}
        {/* K-4 (correção): o cabeçalho mora DENTRO da coluna do conteúdo.
            Antes ficava acima do flex, em largura total: o título começava em
            x=55 e os cards em x=270, porque o menu de seções empurra a coluna
            direita. Título e conteúdo desalinhados na mesma tela.

            O cabeçalho descreve o PAINEL — pertence a ele. */}
        <section className="min-w-0 flex-1" aria-live="polite">
      {/* G-4a: o cabeçalho ACOMPANHA a seção. Antes o h1 era sempre
          "Configurações" e o subtítulo sempre "Escolha uma seção à esquerda" —
          a tela repetia uma ordem que a pessoa já tinha cumprido, e não dizia
          onde ela estava. */}
      {/* K-4: quando há item aberto, o ASSUNTO é o item — o título e a descrição
          são dele, e a seção fica no breadcrumb. Antes a casca dizia "Catálogos"
          e a tela embutida dizia "Tags" logo abaixo: dois títulos para uma
          página só.

          O `actions` vem do slot: é a ação que a tela filha declarou, desenhada
          aqui à direita, no mesmo lugar em todas as telas (decisão do Matheus). */}
      <PageHeader
        daCasca
        crumb={`Configurações · ${atual.nome}`}
        title={
          /* K-5: o "?" ao lado do título da casca — que é a única voz. */
          <span className="inline-flex items-center gap-2">
            {itemAberto?.nome ?? atual.nome}
            <HelpTip
              texto={
                itemAberto
                  ? (itemAberto.ajuda ?? itemAberto.desc)
                  : ajudaDaSecao(atual.id)
              }
            />
          </span>
        }
        subtitle={itemAberto?.desc ?? atual.desc}
        actions={acoes}
      />
          <Outlet />
        </section>
      </div>
    </PageContainer>
  );
}
